import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type BackofficeEmployee = {
  _id: string;
  bp_employee_name: string;
  bp_employee_email_address?: string;
  bp_employee_phone_number?: string;
  bp_employee_role?: string;
  bp_employee_identification_number?: string;
  bp_employee_country?: string;
  bp_employee_county?: string;
  bp_employee_residence?: string;
  bp_employee_profile_picture_url?: string;
  bp_employee_account_information_is_verified?: boolean;
  bp_employee_account_information_is_locked?: boolean;
  bp_employee_locations?: string[];
  [key: string]: any;
};

export interface EmployeesSliceObject {
  items: BackofficeEmployee[];
  loading: boolean;
  error?: string | null;
  total: number;
  page: number;
  pageSize: number;
  selectedEmployeeId?: string | null;
}

const initialState: EmployeesSliceObject = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  pageSize: 100,
  selectedEmployeeId: null,
};

const employeesSlice = createSlice({
  name: 'backofficeEmployees',
  initialState,
  reducers: {
    setEmployees: (
      state,
      action: PayloadAction<{
        items: BackofficeEmployee[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.items = action.payload.items;
      if (typeof action.payload.total === 'number')
        state.total = action.payload.total;
      if (typeof action.payload.page === 'number')
        state.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.pageSize = action.payload.pageSize;
    },
    updateEmployee: (state, action: PayloadAction<BackofficeEmployee>) => {
      const idx = state.items.findIndex(e => e._id === action.payload._id);
      if (idx !== -1)
        state.items[idx] = { ...state.items[idx], ...action.payload };
    },
    removeEmployee: (state, action: PayloadAction<string>) => {
      const deletedId = action.payload;
      const beforeLen = state.items.length;
      state.items = state.items.filter(e => e._id !== deletedId);
      if (state.items.length < beforeLen) {
        state.total = Math.max(0, state.total - 1);
      }
      if (state.selectedEmployeeId === deletedId) {
        state.selectedEmployeeId = null;
      }
    },
    setEmployeesLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setEmployeesError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setEmployeesPagination: (
      state,
      action: PayloadAction<{ page?: number; pageSize?: number }>,
    ) => {
      if (typeof action.payload.page === 'number')
        state.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        state.pageSize = action.payload.pageSize;
    },
    setSelectedEmployeeId: (state, action: PayloadAction<string | null>) => {
      state.selectedEmployeeId = action.payload;
    },
  },
});

export const {
  setEmployees,
  updateEmployee,
  removeEmployee,
  setEmployeesLoading,
  setEmployeesError,
  setEmployeesPagination,
  setSelectedEmployeeId,
} = employeesSlice.actions;

export default employeesSlice.reducer;
